/**
 * Typography Component Utilities
 * Helper functions for typography component logic
 */

import type {
  TTypographyAlign,
  TTypographyColor,
  TTypographyElement,
  TTypographySize,
  TTypographyTransform,
  TTypographyWeight,
} from './types';

/**
 * Get CSS classes for typography size and weight
 */
export const getSizeClasses = (size: TTypographySize, weight: TTypographyWeight): string => {
  return `ap-typography--${size}-${weight}`;
};

/**
 * Get CSS class for typography color
 */
export const getColorClass = (color: TTypographyColor): string => {
  return `ap-typography--color-${color}`;
};

/**
 * Get CSS class for typography alignment
 */
export const getAlignClass = (align: TTypographyAlign): string => {
  return `ap-typography--align-${align}`;
};

/**
 * Get CSS class for typography transform
 */
export const getTransformClass = (transform: TTypographyTransform): string => {
  return transform === 'none' ? '' : `ap-typography--transform-${transform}`;
};

/**
 * Get semantic attributes for accessibility based on element and size
 */
export const getElementSemantics = (
  as: TTypographyElement,
  size: TTypographySize
): { role?: string; 'aria-level'?: number } => {
  // For heading elements, provide appropriate aria-level
  const headingMap: Record<string, number> = {
    h1: 1,
    h2: 2,
    h3: 3,
    h4: 4,
    h5: 5,
    h6: 6,
  };

  if (headingMap[as]) {
    return { 'aria-level': headingMap[as] };
  }

  // For display sizes used with non-heading elements, suggest heading role
  const displaySizes: TTypographySize[] = [
    'display',
    'displayLarge',
    'displayScreen',
    'xxxLarge',
    'xxLarge',
  ];
  if (displaySizes.includes(size) && !headingMap[as]) {
    return { role: 'heading' };
  }

  return {};
};

/**
 * Get recommended semantic element based on typography size
 */
export const getRecommendedElement = (size: TTypographySize): TTypographyElement => {
  const sizeToElementMap: Record<TTypographySize, TTypographyElement> = {
    displayLarge: 'h1',
    display: 'h1',
    displayScreen: 'h1',
    xxxLarge: 'h1',
    xxLarge: 'h2',
    xLarge: 'h3',
    large: 'h4',
    medium: 'h5',
    base: 'p',
    small: 'p',
    xSmall: 'span',
    xxSmall: 'span',
  };

  return sizeToElementMap[size];
};

/**
 * Validate typography props combination
 */
export const validateTypographyProps = (
  as: TTypographyElement,
  size: TTypographySize,
  weight: TTypographyWeight,
  underline: boolean
): { isValid: boolean; warnings: string[] } => {
  const warnings: string[] = [];

  // Check for semantic mismatches
  const recommendedElement = getRecommendedElement(size);
  if (as !== recommendedElement) {
    warnings.push(
      `Size "${size}" is typically used with "${recommendedElement}" element, but you're using "${as}". ` +
        'Consider using the recommended element for better semantics.'
    );
  }

  // Check underline with weight combination (Figma shows underline-600)
  if (underline && weight !== '600') {
    warnings.push(
      'Underline variant is typically used with weight "600" according to design system. ' +
        'Consider using weight="600" for consistency.'
    );
  }

  // Check display sizes with inline prop would be unusual
  const displaySizes: TTypographySize[] = ['display', 'displayLarge', 'displayScreen', 'xxxLarge'];
  if (displaySizes.includes(size)) {
    warnings.push(
      `Display size "${size}" is typically used for block-level headings. ` +
        'Consider using smaller sizes for inline text.'
    );
  }

  return {
    isValid: warnings.length === 0,
    warnings,
  };
};

/**
 * Get line clamp styles
 */
export const getLineClampStyles = (lineClamp: number): React.CSSProperties => {
  return {
    display: '-webkit-box',
    WebkitLineClamp: lineClamp.toString(),
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
};

/**
 * Typography size hierarchy for logical ordering
 */
export const TYPOGRAPHY_SIZE_HIERARCHY: TTypographySize[] = [
  'displayLarge',
  'display',
  'displayScreen',
  'xxxLarge',
  'xxLarge',
  'xLarge',
  'large',
  'medium',
  'base',
  'small',
  'xSmall',
  'xxSmall',
];

/**
 * Get relative size (larger/smaller)
 */
export const getRelativeSize = (
  currentSize: TTypographySize,
  direction: 'larger' | 'smaller'
): TTypographySize | null => {
  const currentIndex = TYPOGRAPHY_SIZE_HIERARCHY.indexOf(currentSize);

  if (currentIndex === -1) return null;

  const newIndex = direction === 'larger' ? currentIndex - 1 : currentIndex + 1;

  if (newIndex < 0 || newIndex >= TYPOGRAPHY_SIZE_HIERARCHY.length) {
    return null;
  }

  return TYPOGRAPHY_SIZE_HIERARCHY[newIndex];
};
